// src/components/events/EventsMiniCard.jsx
//
// Events MiniCard — the front-page "opens in place" tier between the EventsColumn row
// (MicroCard, unchanged) and the standalone EventsDetail page (Detail, unchanged). Reflow,
// never overlay: the consumer (EventsColumn) renders this IN PLACE OF the row it belongs to
// while open (docs/briefs/2026-08-15-events-minicard-replace-and-border.md — replace, not
// supplement; the row is not rendered alongside it), pushing the column down. This component
// exists only while its event is open — there is no "closed MiniCard" state to represent — so
// it has no isOpen prop; the border below applies unconditionally for that reason. It still has
// no opinion on positioning/animation, only its own field layout and its own click-to-collapse,
// so it stays liftable for a future News MiniCard the same way
// useExpandInPlace/useScrollIntoViewOnOpen already are.
//
// Click-to-collapse: clicking anywhere on the open card collapses it back to the MicroCard row
// (the consumer's toggleCard, passed as onCollapse) — except "Full details", which stops
// propagation so it navigates instead of also collapsing.
//
// Context border: a thick all-around border in the Events context colour marks this as the
// open, focused card. "Events context colour" = theme.primary — LAYOUT.md's one documented
// context-colour source (designTokens.js's getContextColor(theme) is exactly theme.primary with
// a fallback, not a distinct value); not a per-column hue. EventsColumn's own Future/Past pill
// and date separators, and MoreButton.jsx's callers (which pass theme.primary directly as
// inlineTextColor), all draw from the same source.
//
// Weight: LAYOUT.md does document a context-colour border precedent in this same file/domain —
// 1px, for EventsColumn's own date-divider/pill definition (LAYOUT.md's "Events layout" row;
// live at EventsColumn.jsx's date-separator and Future/Past pill borders). That precedent is for
// a different job — thin definition against a white background, not marking an active/open
// state — and Michael's own ask was explicitly "a thick border," which 1px would not read as.
// The weight actually used, 4px, instead matches the codebase's one existing "thick border marks
// the active one" convention — MyLocations.jsx's active-area row, `4px solid theme.primary` —
// precedent-by-observed-convention for the same *semantic* use, not by LAYOUT.md token (that
// file sits in a domain LAYOUT.md itself names as not yet token-migrated). Kept as the single
// BORDER_WIDTH constant below so Michael can tune it after eyeballing, per the brief — flagged in
// this build's report as a judgement call between two real but differently-purposed precedents,
// not a silent pick. Exported (16 Aug, Directory column shell brief Scope 3) — the Directory
// concertina's open-section outline reuses this exact binding, not a duplicated "4", so tuning it
// here retunes both surfaces at once.
//
// Field set (docs/briefs/2026-08-15-events-minicard-build-1.md, ruled):
//   image · title · introduction · start time or All day · venue name · venue address · price
//
// D9 empty-field rule — two behaviours, kept as clearly separated per-field blocks below so
// tuning against real data is a one-line change per field, not a rewrite:
//   HOLD     (title, venue name, date/time) — always occupies its layout slot; blank, never
//            placeholder text, when the value is absent.
//   COLLAPSE (introduction, price, venue address, image) — the slot is not rendered at all
//            when the value is absent.
//
// D8 "Full details" affordance — unconditional (addendum-1 to docs/briefs/2026-09-04-news-
// minicard-phase2-build.md, item 2, Michael's ruling — supersedes D8's "iff the record holds
// content beyond the introduction"). Build 1 found the full predicate not reliably computable
// (contact_email/contact_phone/requires_rsvp/rsvp_deadline/event_url/venue_postcode aren't
// fetched by the front-page events query), and the News build's brief had narrowed this to a
// body-only comparison instead — but gating the only route to Detail was guarding against a case
// that doesn't really occur, and risked hiding real Detail-only content (times, contact, RSVP,
// comments) behind a predicate that can't see it. Both surfaces are unconditional again.
//
// D11 — one component, no isMobile branch, no variant. Must survive the narrowest supported
// width; a layout that can't is a fault in this file, not an excuse for a second card.
//
// price (event_details.price_info) — Build 1 found it wasn't fetched anywhere in the front-page
// events pipeline and left it alone pending approval. Michael approved the follow-up (named in
// docs/briefs/2026-08-15-events-minicard-replace-and-border.md's scope boundaries); this build
// adds `price_info` to dataService.getLocalityEvents's two event_details selects (boundary-
// scoped + fallback) and their mapped return objects — a one-column select-list addition on a
// table already being queried there, no schema/RPC change. Still collapses per D9 for any event
// with no price set, same as any other absent value.

import React from 'react';
import { useTheme } from '../../themes/ThemeProvider';
import { useTranslation } from '../../i18n/useTranslation';
import { useTagHouseFallback } from '../../hooks/useTagHouseFallback';
import { parseEventDate } from '../../utils/eventDateTime';
import { tokens } from '../../styles/designTokens';
import StandardButton from '../core/StandardButton';

// Single tunable value — see file header. Matches MyLocations.jsx's active-row precedent.
export const BORDER_WIDTH = 4;

const EventsMiniCard = ({ event, onViewDetails, onCollapse }) => {
  const theme = useTheme();
  const { t, formattingLocale } = useTranslation('events');
  const houseFallback = useTagHouseFallback();


  // COLLAPSE — no inline-SVG placeholder here (unlike the row); an absent image is a hidden
  // slot per D9, not a broken-image graphic.
  const imageUrl = event.cover_image_url || houseFallback || null;

  const introduction = event.summary || '';
  const venueName = event.venue_name || '';
  const venueAddress = event.venue_address || '';
  const price = event.price_info || '';

  // HOLD — "start time or All day", amended (16 Aug, ongoing-grouping brief) to carry a date
  // span instead when the event runs across more than one calendar day: "6–31 Aug" form, both
  // ends via toLocaleDateString so it reads correctly for an ongoing event (started, not yet
  // ended) as well as a future multi-day event. Single-day events (all-day or timed) are
  // unchanged — this only engages when start_date and end_date fall on different calendar days.
  const startDateObj = parseEventDate(event.start_date);
  const endDateObj = event.end_date ? parseEventDate(event.end_date) : null;
  const isMultiDay = !!(startDateObj && endDateObj && startDateObj.toDateString() !== endDateObj.toDateString());

  const dateTimeText = isMultiDay
    ? `${startDateObj.toLocaleDateString(formattingLocale, { day: 'numeric', month: 'short' })}–${endDateObj.toLocaleDateString(formattingLocale, { day: 'numeric', month: 'short' })}`
    : event.is_all_day
      ? t('card_all_day')
      : (startDateObj
          ? startDateObj.toLocaleTimeString(formattingLocale, { hour: '2-digit', minute: '2-digit', hour12: false })
          : '');

  return (
    <div
      onClick={onCollapse}
      style={{
        background: 'white',
        borderRadius: `${tokens.card.radius.list}px`,
        border: `${BORDER_WIDTH}px solid ${theme.primary}`,
        boxShadow: tokens.card.shadow.resting,
        padding: '12px',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        minWidth: 0,
        cursor: onCollapse ? 'pointer' : 'default'
      }}
    >
      {/* Image — COLLAPSE */}
      {imageUrl && (
        <img
          src={imageUrl}
          alt=""
          loading="lazy"
          style={{
            width: '100%',
            maxHeight: '160px',
            objectFit: 'cover',
            borderRadius: `${tokens.card.radius.list}px`,
            backgroundColor: '#f3f4f6'
          }}
          onError={(e) => { e.target.style.display = 'none'; }}
        />
      )}

      {/* Title — HOLD */}
      <div
        style={{
          fontFamily: 'OpenSauceSans, sans-serif',
          fontWeight: '700',
          fontSize: '15px',
          color: '#1f2937',
          lineHeight: '1.3',
          minHeight: '1.3em'
        }}
      >
        {event.title || ''}
      </div>

      {/* Introduction — COLLAPSE */}
      {introduction && (
        <div
          style={{
            fontFamily: 'OpenSauceSans, sans-serif',
            fontSize: '13px',
            color: '#4b5563',
            lineHeight: '1.5'
          }}
        >
          {introduction}
        </div>
      )}

      {/* Start time or All day — HOLD */}
      <div
        style={{
          fontFamily: 'OpenSauceSans, sans-serif',
          fontWeight: '600',
          fontSize: '13px',
          color: theme.primary,
          minHeight: '1.4em'
        }}
      >
        {dateTimeText}
      </div>

      {/* Venue name — HOLD */}
      <div
        style={{
          fontFamily: 'OpenSauceSans, sans-serif',
          fontWeight: '600',
          fontSize: '13px',
          color: '#1f2937',
          minHeight: '1.4em'
        }}
      >
        {venueName}
      </div>

      {/* Venue address — COLLAPSE */}
      {venueAddress && (
        <div
          style={{
            fontFamily: 'OpenSauceSans, sans-serif',
            fontSize: '12px',
            color: '#6b7280'
          }}
        >
          {venueAddress}
        </div>
      )}

      {/* Price — COLLAPSE (now fetched, per file header; still collapses for any event with none set) */}
      {price && (
        <div
          style={{
            fontFamily: 'OpenSauceSans, sans-serif',
            fontWeight: '600',
            fontSize: '12px',
            color: '#1f2937'
          }}
        >
          {price}
        </div>
      )}

      {/* "Full details" — unconditional (see file header). Stops propagation so it navigates
          instead of also collapsing the card via the container's onClick above.
          variant="inline-emphasis" (16 Aug correction — was "inline", too small/quiet):
          bold, ~20% larger than a plain inline control, in the card's context colour via
          inlineTextColor — the reusable treatment defined in StandardButton.jsx/
          designTokens.js, not an Events-local style. News's MiniCard uses the same variant. */}
      <div style={{ marginTop: '2px' }} onClick={(e) => e.stopPropagation()}>
        <StandardButton
          variant="inline-emphasis"
          iconName="ChevronRight"
          label={t('card_full_details')}
          onClick={onViewDetails}
          inlineTextColor={theme.primary}
        />
      </div>
    </div>
  );
};

export default EventsMiniCard;
